/**
 * Portuguese NIF (Número de Identificação Fiscal) validation utility
 * 
 * The NIF validation follows the Portuguese algorithm for fiscal identification numbers.
 * A valid NIF has 9 digits where the 9th digit is a check digit calculated from the first 8.
 */

/**
 * Validates a Portuguese NIF (Número de Identificação Fiscal)
 * @param {string} nif - The NIF to validate (can contain spaces or formatting)
 * @returns {boolean} - True if the NIF is valid, false otherwise
 */
export const validateNif = (nif) => {
  if (!nif || typeof nif !== 'string') {
    return false;
  }

  // Remove any spaces or formatting
  const cleanNif = nif.replace(/\s/g, '');
  
  // Check if it's exactly 9 digits
  const re = /^\d{9}$/;
  if (!re.test(cleanNif)) {
    return false;
  }
  
  // Portuguese NIF validation algorithm
  const digits = cleanNif.split('').map(Number);
  const checkDigit = digits[8];
  
  // Calculate check digit using the official Portuguese algorithm
  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += digits[i] * (9 - i);
  }
  
  const remainder = sum % 11;
  let calculatedCheckDigit;
  
  if (remainder < 2) {
    calculatedCheckDigit = 0;
  } else {
    calculatedCheckDigit = 11 - remainder;
  }
  
  return calculatedCheckDigit === checkDigit;
};

/**
 * Formats a NIF for display (adds spaces for readability)
 * @param {string} nif - The NIF to format
 * @returns {string} - Formatted NIF (XXX XXX XXX)
 */
export const formatNif = (nif) => {
  if (!nif || typeof nif !== 'string') {
    return '';
  }

  const cleanNif = nif.replace(/\s/g, '');
  
  if (cleanNif.length === 9) {
    return `${cleanNif.slice(0, 3)} ${cleanNif.slice(3, 6)} ${cleanNif.slice(6, 9)}`;
  }
  
  return cleanNif;
};

/**
 * Cleans a NIF by removing all non-digit characters
 * @param {string} nif - The NIF to clean
 * @returns {string} - Cleaned NIF (only digits)
 */
export const cleanNif = (nif) => {
  if (!nif || typeof nif !== 'string') {
    return '';
  }

  return nif.replace(/\D/g, '');
};
